import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import React, { useMemo } from 'react';
import {
	Box,
	DialogContent,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Switch,
	FormControlLabel,
	Autocomplete,
	TableCell,
	TableRow,
	TableContainer,
	Table,
	TableBody,
	Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { DatePickerComponent } from 'ui/DatePickerComponent';
import { CountrySelectMenu } from 'ui/CountrySelectMenu';
import { ETableStructureType, ItemWithId } from 'ui/EasTable/types';
import {
	EEmEnums,
	FEES_CLASSES,
	REPORTING_FEE_TYPE_ID,
} from 'redux/reducers/emManagement/constants';
import {
	getEnumsAnnualFeeTypeId,
	getEnumsInvoiceFeeTypeId,
	getEnumsFeeClass,
	getFeeClasses,
	getMonthlyFees,
	getEnumsTimezoneList,
	getEnumsWeightUnit,
	getEnumsIossSaleOverThresholdOption,
	getEnumsIossDutyMode,
	getEnumsPaymentMode,
	getEnumsCustomsDataStatus,
	getEnumsSupportedCurrency,
	getEnumsReportingFeeActual,
	getEnumsSiteIntegrationType,
	getEnumsSitePlatform,
	getEnumsLegalStatusEu,
	getEnumsManualFeeClass,
	getEnumsMonthlyFees,
} from 'redux/reducers/emManagement/selectors';
import { getCountryListData } from 'redux/reducers/country/selectors';
import { TEmEnumItem, TFeeClassItem, TMonthlyFeeItem } from 'redux/reducers/emManagement/types';
import { UserAvatarMenu } from 'ui/UserAvatarMenu';
import { TUser } from 'redux/reducers/user/types';
import moment from 'moment-timezone';
import { TBoxStructureItem } from '../types';
import { TEditParamsDialogProps } from './types';
import { validationSchema } from './validationSchema';
import { updateEmerchantRequest } from '../../../../../redux/reducers/emManagement/reducer';
import { EDatePickerAllowedRange } from '../../../../../ui/DatePickerComponent/constants';

export const EditParamsDialog = <T extends ItemWithId>({
	open,
	setClose,
	data,
	boxStructure,
	onSave,
	title,
	columnNumber = 1,
	utcDateOnlySelectors = [],
}: TEditParamsDialogProps<T>) => {
	const countryList = useSelector(getCountryListData);
	const enumsAnnual = useSelector(getEnumsAnnualFeeTypeId);
	const enumsInvoice = useSelector(getEnumsInvoiceFeeTypeId);
	const enumsFeeClass = useSelector(getEnumsFeeClass);
	const enumsFeeClasses = useSelector(getFeeClasses);
	const enumsReportingFeeType = useSelector(getMonthlyFees);
	// const enumsTimezoneList = useSelector(getEnumsTimezoneList);
	const enumsWeightUnit = useSelector(getEnumsWeightUnit);
	const enumsCustomsDataStatus = useSelector(getEnumsCustomsDataStatus);
	const enumsPaymentMode = useSelector(getEnumsPaymentMode);
	const enumsIossSaleOverThresholdOption = useSelector(getEnumsIossSaleOverThresholdOption);
	const enumsSupportedCurrency = useSelector(getEnumsSupportedCurrency);
	const enumsReportingFeeActual = useSelector(getEnumsReportingFeeActual);
	const enumsSiteIntegrationType = useSelector(getEnumsSiteIntegrationType);
	const enumsSitePlatform = useSelector(getEnumsSitePlatform);
	const enumsLegalStatusEu = useSelector(getEnumsLegalStatusEu);
	const enumsManualFeeClass = useSelector(getEnumsManualFeeClass);
	const enumsMonthlyFees = useSelector(getEnumsMonthlyFees);
	const enumsIossDutyMode = useSelector(getEnumsIossDutyMode);
	const bodyArray = boxStructure.body.filter((item) => item.editable !== false);
	const enumsTimezoneList = moment.tz.names();

	const dispatch = useDispatch();

	const getDefaultFieldValue = (type: ETableStructureType): unknown => {
		if (type === ETableStructureType.BOOLEAN) return false;
		if (type === ETableStructureType.USERS) return [];
		return '';
	};

	const initialFormValues = useMemo(() => {
		if (!open) return {};
		const initialValues: Record<string, unknown> = {};
		boxStructure.body.forEach((item: TBoxStructureItem<T>) => {
			if (item.editable) {
				const fieldName = String(item.selector);
				if (data) {
					const rawValue = data[fieldName as keyof typeof data] as unknown;
					initialValues[fieldName] =
						rawValue !== undefined && rawValue !== null
							? rawValue
							: getDefaultFieldValue(item.type);
				} else {
					initialValues[fieldName] = getDefaultFieldValue(item.type);
				}
			}
		});
		return initialValues;
	}, [data, open, boxStructure]);

	const reset = () => {
		// Form resets via initialFormValues when dialog closes (open -> false)
	};

	const handleSave = (values: Record<string, unknown>) => {
		if (onSave) {
			if (!data) {
				onSave(values);
				return;
			}

			const areEqual = (a: unknown, b: unknown): boolean => {
				try {
					return JSON.stringify(a) === JSON.stringify(b);
				} catch {
					return a === b;
				}
			};

			const hasChanges = Object.keys(values).some(
				(key) => !areEqual(values[key], initialFormValues[key]),
			);

			if (!hasChanges) return;

			onSave(values);
			return;
		}

		if (!data) return;

		const areEqual = (a: unknown, b: unknown): boolean => {
			try {
				return JSON.stringify(a) === JSON.stringify(b);
			} catch {
				return a === b;
			}
		};

		const changed: Record<string, unknown> = {};
		Object.keys(values).forEach((key: string) => {
			const newValue = values[key];
			const initialValue = initialFormValues[key];

			if (key === 'supportManager') {
				const getIdString = (v: unknown): string | null => {
					if (v && typeof v === 'object' && 'id' in (v as Record<string, unknown>)) {
						const raw = (v as { id?: number | string | null }).id;
						return raw == null ? null : String(raw);
					}
					return null;
				};
				const preferNumberIfNumeric = (s: string | null): number | string | null => {
					if (s == null) return null;
					const trimmed = s.trim();
					if (/^\d+$/.test(trimmed)) return parseInt(trimmed, 10);
					return trimmed;
				};
				const newIdStr = getIdString(newValue);
				const initialIdStr = getIdString(initialValue);
				if (newIdStr !== initialIdStr) {
					changed.supportManager = preferNumberIfNumeric(newIdStr);
				}
				return;
			}

			if (!areEqual(newValue, initialValue)) {
				changed[key] = newValue;
			}
		});

		// noop if nothing changed
		if (Object.keys(changed).length === 0) {
			return;
		}

		dispatch(updateEmerchantRequest({ id: data.id, data: changed }));
		// dispatch(updateEmRequest({ id: items.id, data: changed }));
	};

	const formik = useFormik({
		initialValues: initialFormValues,
		enableReinitialize: true,
		validationSchema,
		onSubmit: () => {
			handleSave(formik?.values);
			formik.resetForm();
			reset();
			setClose();
		},
	});

	const renderField = (item: TBoxStructureItem<T>) => {
		const fieldName = String(item.selector);
		const fieldValue = formik.values[fieldName];

		const resolveEnumOptions = (
			enumName?: EEmEnums | typeof FEES_CLASSES | typeof REPORTING_FEE_TYPE_ID,
		): { value: string; label: string }[] => {
			if (!enumName) return [];
			if (enumName === EEmEnums.ANNUAL_FEE_TYPE_ID) {
				return (
					enumsAnnual?.map((i: TEmEnumItem) => ({
						value: i.value,
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.INVOICE_FEE_TYPE_ID) {
				return (
					enumsInvoice?.map((i: TEmEnumItem) => ({
						value: i.value,
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.FEES_CLASS) {
				return (
					enumsFeeClass?.map((i: TEmEnumItem) => ({
						value: i.value,
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === FEES_CLASSES) {
				return (
					enumsFeeClasses?.map((i: TFeeClassItem) => ({
						value: String(i.id),
						label: i.description,
					})) ?? []
				);
			}
			if (enumName === REPORTING_FEE_TYPE_ID) {
				return (
					enumsReportingFeeType?.map((i: TMonthlyFeeItem) => ({
						value: String(i.id),
						label: i.description,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.TIMEZONE_LIST) {
				return (
					enumsTimezoneList?.map((tz: string) => ({
						value: tz,
						label: `${tz} (${moment.tz(tz).format('Z')})`,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.WEIGHT_UNIT) {
				return (
					enumsWeightUnit?.map((i: TEmEnumItem) => ({
						value: String(i.value),
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.CUSTOMS_DATA_STATUS) {
				return (
					enumsCustomsDataStatus?.map((i: TEmEnumItem) => ({
						value: String(i.value),
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.PAYMENT_MODE) {
				return (
					enumsPaymentMode?.map((i: TEmEnumItem) => ({
						value: String(i.value),
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.IOSS_SALE_OVER_THRESHOLD_OPTION) {
				return (
					enumsIossSaleOverThresholdOption?.map((i: TEmEnumItem) => ({
						value: String(i.value),
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.IOSS_DUTY_MODES) {
				return (
					enumsIossDutyMode?.map((iossDutyModeItem: TEmEnumItem) => ({
						value: String(iossDutyModeItem.value),
						label: iossDutyModeItem.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.SUPPORTED_CURRENCY) {
				return (
					enumsSupportedCurrency?.map((i: TEmEnumItem) => ({
						value: i.value,
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.EM_REPORTING_FEE_ACTUAL) {
				return (
					enumsReportingFeeActual?.map((i: TEmEnumItem) => ({
						value: i.value,
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.SITE_INTEGRATION_TYPE) {
				return (
					enumsSiteIntegrationType?.map((i: TEmEnumItem) => ({
						value: i.value,
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.SITE_PLATFORM) {
				return (
					enumsSitePlatform?.map((i: TEmEnumItem) => ({
						value: i.value,
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.LEGAL_STATUS_EU) {
				return (
					enumsLegalStatusEu?.map((i: TEmEnumItem) => ({
						value: i.value,
						label: i.displayName,
					})) ?? []
				);
			}
			if (enumName === EEmEnums.MANUAL_FEE_CLASS) {
				return (
					enumsManualFeeClass?.map((i: TEmEnumItem) => ({
						value: i.value,
						label: i.displayName,
					})) ?? []
				);
			}

			return [];
		};
		switch (item.type) {
			case ETableStructureType.ARRAY_ID: {
				const options = item.enumNameForEditing
					? resolveEnumOptions(item.enumNameForEditing)
					: resolveEnumOptions(item.enumName);
				const selectedOption =
					fieldValue === ''
						? null
						: (options.find((o) => String(o.value) === String(fieldValue)) ?? null);
				return (
					<FormControl fullWidth size="small">
						<Autocomplete
							id={fieldName}
							size="small"
							options={options}
							value={selectedOption}
							onChange={(event, newValue) => {
								let nextValue: string | number | null = newValue ? newValue.value : null;
								if (
									item.enumName === REPORTING_FEE_TYPE_ID ||
									item.enumName === EEmEnums.ANNUAL_FEE_TYPE_ID ||
									item.enumName === EEmEnums.TIMEZONE_LIST ||
									item.enumName === EEmEnums.WEIGHT_UNIT ||
									item.enumName === EEmEnums.CUSTOMS_DATA_STATUS ||
									item.enumName === EEmEnums.PAYMENT_MODE ||
									item.enumName === EEmEnums.IOSS_SALE_OVER_THRESHOLD_OPTION ||
									item.enumName === EEmEnums.EM_REPORTING_FEE_ACTUAL ||
									item.enumName === EEmEnums.SITE_INTEGRATION_TYPE ||
									item.enumName === EEmEnums.SITE_PLATFORM ||
									item.enumName === EEmEnums.MONTHLY_FEES ||
									item.enumName === EEmEnums.IOSS_DUTY_MODES
								) {
									nextValue = nextValue === null ? null : String(nextValue);
								} else if (
									item.enumName === EEmEnums.FEES_CLASS ||
									item.enumName === EEmEnums.INVOICE_FEE_TYPE_ID ||
									item.enumName === EEmEnums.SUPPORTED_CURRENCY ||
									item.enumName === EEmEnums.MANUAL_FEE_CLASS
								) {
									nextValue = nextValue === null ? null : Number(nextValue);
								}
								formik.setFieldValue(fieldName, nextValue).catch(() => {});
							}}
							getOptionLabel={(option) => option.label ?? ''}
							isOptionEqualToValue={(opt, val) => String(opt.value) === String(val.value)}
							renderInput={(params) => <TextField {...params} label={item.name} />}
							fullWidth
						/>
					</FormControl>
				);
			}
			case ETableStructureType.BOOLEAN:
				return (
					<FormControlLabel
						control={
							<Switch
								checked={fieldValue === true || fieldValue === 'true'}
								onChange={(e) => {
									formik.setFieldValue(fieldName, e.target.checked).catch(() => {});
								}}
								name={fieldName}
							/>
						}
						label={item.name}
					/>
				);

			case ETableStructureType.DATE: {
				const isUtcDateOnly = utcDateOnlySelectors.includes(fieldName);

				return (
					<DatePickerComponent
						id={fieldName}
						label={item.name}
						allowedRange={
							isUtcDateOnly ? EDatePickerAllowedRange.FUTURE : EDatePickerAllowedRange.ALL
						}
						value={
							fieldValue
								? isUtcDateOnly
									? DateTime.fromISO(String(fieldValue).slice(0, 10), { zone: 'utc' })
									: DateTime.fromISO(String(fieldValue))
								: null
						}
						onChange={(date) => {
							const isoValue = date
								? isUtcDateOnly
									? DateTime.utc(date.year, date.month, date.day).toISO({
											suppressMilliseconds: false,
										})
									: date.toUTC().toISO({ suppressMilliseconds: false })
								: '';
							formik.setFieldValue(fieldName, isoValue || '').catch(() => {});
						}}
					/>
				);
			}

			case ETableStructureType.COUNTRY_CODE:
				return (
					<CountrySelectMenu
						countryList={
							countryList?.map((countryItem) => ({
								code: countryItem.isoAlpha2,
								label: countryItem.name,
								number: countryItem.id,
							})) ?? []
						}
						onChange={() => () => {}}
						onChangeNumber={(countryNumber: number) => {
							formik.setFieldValue(fieldName, countryNumber).catch(() => {});
						}}
						countryCode={typeof fieldValue === 'string' ? fieldValue : ''}
						size="small"
						type={ETableStructureType.COUNTRY_CODE}
					/>
				);

			case ETableStructureType.COUNTRY_NUMBER:
				return (
					<CountrySelectMenu
						countryList={
							countryList?.map((countryItem) => ({
								code: countryItem.isoAlpha2,
								label: countryItem.name,
								number: countryItem.id,
							})) ?? []
						}
						onChangeNumber={(countryNumber: number) => {
							formik.setFieldValue(fieldName, countryNumber).catch(() => {});
						}}
						countryNumber={fieldValue as number}
						size="small"
						type={ETableStructureType.COUNTRY_NUMBER}
					/>
				);

			case ETableStructureType.TEXT:
			case ETableStructureType.MULTILINE_TEXT:
				return (
					<TextField
						id={fieldName}
						name={fieldName}
						size="small"
						label={item.name}
						value={fieldValue ?? ''}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						multiline
						rows={3}
						fullWidth
						error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
						helperText={formik.touched[fieldName] && (formik.errors[fieldName] as string)}
					/>
				);
			case ETableStructureType.AMOUNT:
			case ETableStructureType.NUMBER:
				return (
					<TextField
						id={fieldName}
						name={fieldName}
						size="small"
						label={item.name}
						value={fieldValue ?? ''}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						type="number"
						fullWidth
						error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
						helperText={formik.touched[fieldName] && (formik.errors[fieldName] as string)}
					/>
				);

			case ETableStructureType.STATUS:
				return (
					<FormControl fullWidth size="small">
						<InputLabel id={`${fieldName}-label`}>{item.name}</InputLabel>
						<Select
							id={fieldName}
							labelId={`${fieldName}-label`}
							label={item.name}
							name={fieldName}
							value={fieldValue ?? ''}
							onChange={formik.handleChange}
						>
							<MenuItem value="Active">Active</MenuItem>
							<MenuItem value="Deactivated">Deactivated</MenuItem>
							<MenuItem value="Blocked">Blocked</MenuItem>
						</Select>
					</FormControl>
				);
			case ETableStructureType.USER:
				return (
					<UserAvatarMenu
						user={
							typeof fieldValue === 'object' && fieldValue !== null ? (fieldValue as TUser) : null
						}
						size="sm"
						onClickUser={(user) => {
							formik.setFieldValue(fieldName, user ?? '').catch(() => {});
						}}
					/>
				);

			case ETableStructureType.USERS:
				//
				return (
					<Box
						sx={{
							display: 'flex',
							flexDirection: 'row',
							gap: '4px',
							justifyContent: 'flex-start',
						}}
					>
						{(Array.isArray(fieldValue) ? (fieldValue as TUser[]) : []).map(
							(user: TUser, userIndex: number) => (
								<UserAvatarMenu
									key={user.id}
									user={user}
									size="sm"
									onClickUser={(newUser) => {
										const currentUsers: TUser[] = Array.isArray(fieldValue)
											? (fieldValue as TUser[])
											: [];
										const nextUsers = [...currentUsers];
										if (newUser) {
											nextUsers[userIndex] = newUser;
										}
										formik?.setFieldValue(fieldName, nextUsers).catch(() => {});
									}}
									delateButton
									onDeleteUser={() => {
										const currentUsers: TUser[] = Array.isArray(fieldValue)
											? (fieldValue as TUser[])
											: [];
										const nextUsers = [...currentUsers];
										nextUsers.splice(userIndex, 1);
										formik?.setFieldValue(fieldName, nextUsers).catch(() => {});
									}}
								/>
							),
						)}
						<Button
							type="button"
							size="small"
							color="primary"
							sx={{
								fontWeight: 550,
								fontSize: '0.9rem',
								padding: '2px 10px',
								textTransform: 'none',
								borderRadius: '12px',
								border: '1px dashed #016193',
							}}
							onClick={() => {
								const currentUsers: TUser[] = Array.isArray(fieldValue)
									? (fieldValue as TUser[])
									: [];
								const nextUsers = [
									...currentUsers,
									{
										id: 'new-user',
										fullName: 'New User',
										email: '',
									},
								];
								formik.setFieldValue(fieldName, nextUsers).catch(() => {});
							}}
						>
							Add User +
						</Button>
					</Box>
				);

			default:
				return (
					<TextField
						id={fieldName}
						name={fieldName}
						size="small"
						label={item.name}
						value={fieldValue ?? ''}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						fullWidth
						error={formik.touched[fieldName] && Boolean(formik.errors[fieldName])}
						helperText={formik.touched[fieldName] && (formik.errors[fieldName] as string)}
					/>
				);
		}
	};

	return (
		<Dialog
			open={open}
			sx={{
				'& .MuiDialog-paper': {
					width: '800px',
					maxWidth: '800px',
					borderRadius: '12px',
					padding: '12px',
				},
			}}
		>
			<DialogTitle
				color="primary"
				dangerouslySetInnerHTML={{ __html: title || `Edit ${String(boxStructure.title)}` }}
			/>
			<form onSubmit={formik?.handleSubmit}>
				<DialogContent
					sx={{
						p: '12px 12px 12px 12px',
					}}
				>
					{/* <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
						{boxStructure.body
							?.filter((item: TBoxStructureItem<T>) => item.editable)
							?.map((item: TBoxStructureItem<T>) => (
								<Box key={String(item.selector)}>{renderField(item)}</Box>
							))}
					</Box> */}
					<Box
						sx={{
							padding: '0px',
							paddingBottom: '0px',
							display: 'flex',
							flexDirection: 'column',
							gap: '24px',
						}}
					>
						{Array.from({ length: Math.ceil(bodyArray.length / columnNumber) }).map(
							(_, rowIndex) => {
								const rowItems = bodyArray.slice(
									rowIndex * columnNumber,
									(rowIndex + 1) * columnNumber,
								);
								return (
									<Box key={`row-${rowItems[0]?.name || rowIndex}`}>
										{rowItems.map((item, colIndex) => {
											return (
												<React.Fragment key={String(item.selector)}>
													<Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
														{renderField(item)}
														{item.description && (
															<Box
																sx={{
																	// backgroundColor: 'warning.light',
																	padding: '4px',
																	borderRadius: '4px',
																}}
															>
																<Typography
																	variant="body2"
																	color="text.secondary"
																	sx={{ fontSize: '0.85rem' }}
																>
																	{item.description}
																</Typography>
															</Box>
														)}
													</Box>
												</React.Fragment>
											);
										})}
									</Box>
								);
							},
						)}
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						type="button"
						onClick={() => {
							formik.resetForm();
							reset();
							setClose();
						}}
					>
						Cancel
					</Button>
					<Button type="submit" autoFocus variant="contained">
						Save
					</Button>
				</DialogActions>
			</form>
		</Dialog>
	);
};
